/**
 * One source for "may this login see this page".
 *
 * The sidebar hid links it had no permission for, but nothing stopped anyone
 * typing the URL — canAccessRoute returns true for every non-admin role and
 * defers to "the Sidebar and my-permissions". Hiding a link is not the same as
 * refusing the page, so the guard consults the same map the sidebar does.
 *
 * The fetch is cached at module scope: the sidebar and the route guard both
 * ask on every render, and this is one answer per session, not one per mount.
 */
import { useEffect, useState } from 'react';

export const API_BASE = process.env.REACT_APP_API_URL
  || (window.location.protocol === 'https:'
        ? `${window.location.protocol}//${window.location.host}`
        : `http://${window.location.hostname}:8000`);

let cache = null;      // resolved permissions map
let inflight = null;   // de-duplicates concurrent callers

export function clearPagePermissions() { cache = null; inflight = null; }

export function fetchPagePermissions() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(null);
  inflight = fetch(`${API_BASE}/page-permissions/my-permissions`,
                   { headers: { Authorization: `Bearer ${token}` } })
    .then(r => (r.ok ? r.json() : null))
    .then(d => { cache = d; inflight = null; return d; })
    .catch(() => { inflight = null; return null; });
  return inflight;
}

export function usePagePermissions() {
  const [permissions, setPermissions] = useState(cache);
  const [loading, setLoading] = useState(!cache);
  useEffect(() => {
    let alive = true;
    if (cache) { setPermissions(cache); setLoading(false); return; }
    fetchPagePermissions().then(d => {
      if (!alive) return;
      setPermissions(d); setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  return { permissions, loading };
}

/**
 * What this login may do on one page.
 *
 * A screen used to render its delete icon unconditionally and let the server
 * refuse the click. That is a worse experience than not offering it: the
 * button looks like a thing you can do right up until it isn't. Ask here and
 * render accordingly.
 *
 *   const { canEdit, canDelete } = usePagePermission('settings/users');
 *
 * Admin, superadmin and administration get everything, matching the server's
 * own short-circuit — the grid governs the other roles.
 */
export function usePagePermission(pageCode) {
  const { permissions, loading } = usePagePermissions();
  let role = '';
  try { role = (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); } catch {}
  const unrestricted = ['admin', 'superadmin', 'administration'].includes(role);
  const entry = (permissions || {})[pageCode] || {};
  return {
    loading,
    canView:   unrestricted || !!entry.can_view,
    canEdit:   unrestricted || !!entry.can_edit,
    // Fail CLOSED while the answer is still in flight, so a delete icon never
    // flashes into existence for someone who is about to be told no.
    canDelete: unrestricted || (!loading && !!entry.can_delete),
  };
}

/**
 * Sidebar path -> permission code.
 *
 * Aliases are only for paths with no code of their own. Every alias that
 * pointed a real page at a DIFFERENT page's code was removed, because it
 * silently widened access: 'google-workspace' rode on 'documents', and nine
 * 'rcm/*' paths all rode on 'rcm'.
 */
const ALIASES = {
  'saas-billing': 'settings',
};

export function pathToPermCode(path) {
  if (!path) return null;
  const code = String(path).replace(/^\//, '');
  return ALIASES[code] || code;
}

/**
 * Whether a full browser path is viewable. Detail routes (/patients/123) are
 * governed by their parent page, so trailing segments are dropped until a
 * known code is found. A path matching NO known code is allowed through —
 * this guard refuses what is explicitly denied, it does not invent denials.
 */
export function canViewPath(permissions, role, path) {
  const r = (role || '').toLowerCase();
  if (r === 'admin' || r === 'superadmin' || r === 'platform_admin') return true;
  if (!permissions) return true;                 // still loading — do not flash a denial
  let parts = String(path || '').replace(/^\//, '').split('/').filter(Boolean);
  while (parts.length) {
    const perm = permissions[pathToPermCode(parts.join('/'))];
    if (perm) return !!perm.can_view;
    parts.pop();
  }
  return true;
}
